import type { FeedItem } from '@/components/ActivityFeed/ActivityFeed';
import type { PersonCardResponse } from '@/models/persons';
import { buildContactFeed } from '@/pages/contacts/feed';
import { buildLeadFeed } from '@/pages/leads/feed';

const minutes = (s: number | null) =>
  s == null ? '' : ` (${Math.max(1, Math.round(s / 60))} min)`;

export function buildPersonFeed(feed: PersonCardResponse['feed']): FeedItem[] {
  const person = buildContactFeed(feed.personEvents, feed.personComments).map(
    (i) => ({ ...i, id: `person-${i.id}` }),
  );
  const leadIds = [
    ...new Set([
      ...feed.requestEvents.map((e) => Number(e.leadId)),
      ...feed.requestComments.map((c) => Number(c.leadId)),
    ]),
  ];
  const requests = leadIds.flatMap((leadId) =>
    buildLeadFeed(
      feed.requestEvents.filter((e) => Number(e.leadId) === leadId),
      feed.requestComments.filter((c) => Number(c.leadId) === leadId),
    ).map((i) => ({
      ...i,
      id: `lead-${leadId}-${i.id}`,
      title: `#${leadId} · ${i.title}`,
    })),
  );
  const calls: FeedItem[] = feed.calls.map((c) => ({
    id: `call-${c.id}`,
    at: c.createdAt,
    actor: c.answeredByName ?? c.answeredByEmail ?? null,
    kind: 'system',
    title: `${c.leadId ? `#${c.leadId} · ` : ''}${
      c.direction === 'inbound' ? 'Inbound' : 'Outbound'
    } call, ${c.disposition}${minutes(c.durationSeconds)}`,
  }));
  const offers: FeedItem[] = feed.offers.flatMap((o) => [
    {
      id: `offer-${o.id}`,
      at: o.createdAt,
      actor: null,
      kind: 'system' as const,
      title: `Offer #${o.id} created: ${o.title}`,
    },
    ...(o.sentAt
      ? [
          {
            id: `offer-sent-${o.id}`,
            at: o.sentAt,
            actor: null,
            kind: 'system' as const,
            title: `Offer #${o.id} sent`,
          },
        ]
      : []),
  ]);
  const invoices: FeedItem[] = feed.invoices.map((i) => ({
    id: `invoice-${i.id}`,
    at: i.createdAt,
    actor: null,
    kind: 'system',
    title: `Invoice ${i.invoiceNumber} (offer #${i.offerId}): ${i.status}, ${i.totalAmount} ${i.currency}`,
  }));
  const activities: FeedItem[] = feed.activities.map((a) => ({
    id: `activity-${a.id}`,
    at: a.at,
    actor: a.authorName ?? a.authorEmail ?? a.mondayAuthor ?? null,
    kind: 'comment',
    title: `${a.leadId ? `#${a.leadId} · ` : ''}Touch: ${a.kind}${
      a.status === 'open' ? ' (open)' : ''
    }`,
    body: a.body ?? undefined,
  }));
  return [
    ...person,
    ...requests,
    ...calls,
    ...activities,
    ...offers,
    ...invoices,
  ];
}
